import { plainText } from "../../atoms/text/text";
import type { ChannelSearchItem } from "../../molecules/channel-search/channel-search.shared";
import { isSafeImageSrc } from "../../services/safe-image-src";
import type { LineupChannel, LineupPack, LineupSubscription } from "./channel-lineup.types";
import { type LocalityKind, localityKindFor } from "./locality-badge";

/**
 * Channel Lineup search — the two pure functions that connect the lineup's data to
 * `molecules/channel-search` (SR3, SR4, ZP12).
 */

/**
 * A search item's `id` is namespaced, and the namespace is the only thing added to it.
 * `channel:<CCID>` and `subscription:<plan code>` come from (kind, id) — never from a label — so
 * the identity rule holds unchanged (`channel-search.shared.tsx:24-39`).
 */
export const LINEUP_SEARCH_KINDS = ["channel", "subscription"] as const;
export type LineupSearchKind = (typeof LINEUP_SEARCH_KINDS)[number];

export interface LineupSearchRef {
  kind: LineupSearchKind;
  /** The underlying CCID or Epoch plan code, namespace removed. */
  id: string;
}

export function lineupSearchId(kind: LineupSearchKind, id: string): string {
  return `${kind}:${id}`;
}

/**
 * Split on the first colon only. A ccid or a plan code may legitimately contain one, and
 * `split(":")` would silently truncate it.
 */
export function parseLineupSearchId(value: string): LineupSearchRef | undefined {
  const boundary = value.indexOf(":");
  if (boundary < 0) {
    return undefined;
  }
  const kind = value.slice(0, boundary);
  const id = value.slice(boundary + 1);
  if (id === "" || !LINEUP_SEARCH_KINDS.includes(kind as LineupSearchKind)) {
    return undefined;
  }
  return {
    kind: kind as LineupSearchKind,
    id
  };
}

/** The resolved slices the suggestion list is built from. No status field here, deliberately. */
export interface LineupSearchSource {
  /** The model's own channels — nationals, and anything else it carries. */
  channels?: readonly LineupChannel[];
  /** SR3 — `…:locals:<dma>`, the current ZIP's local channels. */
  localChannels?: readonly LineupChannel[];
  /** SR3 — the beam in-market RSN call, mapped at the seam (G1). */
  rsns?: readonly LineupChannel[];
  /**
   * SR4 — the page's own subscription list. Seeded first, so that when the same subscription also
   * sits on a Genre Pack it is offered once under the page's name for it.
   */
  subscriptions?: readonly LineupSubscription[];
  /** SR4 — every Genre Pack; each pack's `subscriptions[]` is folded in. */
  packs?: readonly LineupPack[];
}

export interface LineupSearchItemOptions {
  /** SR4/SR5 — the heading subscription rows sit under. */
  subscriptionsGroupHeading?: string;
  /** GR4/ZP9 — the badge wording per kind; a kind with none carries no badge. */
  badgeLabels?: Partial<Record<LocalityKind, string>>;
}

function channelItem(
  channel: LineupChannel,
  badgeLabels: Partial<Record<LocalityKind, string>>
): ChannelSearchItem {
  const locality = localityKindFor(channel);
  const badge = locality === null ? undefined : badgeLabels[locality]?.trim();

  return {
    id: lineupSearchId("channel", channel.id),
    /* Every feed string through `plainText`: "Telemundo East<br/>" is a live
       fixture value, and the label is both drawn and matched against. */
    label: plainText(channel.name),
    secondary: channel.number === undefined ? undefined : plainText(channel.number),
    badge: badge === undefined || badge === "" ? undefined : plainText(badge),
    /* Every image sink through `isSafeImageSrc`. */
    iconSrc: isSafeImageSrc(channel.logoUrl) ? channel.logoUrl : undefined
  };
}

function subscriptionItem(
  subscription: LineupSubscription,
  group: string
): ChannelSearchItem {
  return {
    id: lineupSearchId("subscription", subscription.id),
    label: plainText(subscription.name),
    group: group === "" ? undefined : group,
    iconSrc: isSafeImageSrc(subscription.logoUrl) ? subscription.logoUrl : undefined
  };
}

/** SR3 + SR4 — everything the reader can find, in the order the suggestion list offers it. */
export function buildLineupSearchItems(
  source: LineupSearchSource,
  options: LineupSearchItemOptions = {}
): ChannelSearchItem[] {
  const group = options.subscriptionsGroupHeading?.trim() ?? "";
  const badgeLabels = options.badgeLabels ?? {};

  const items: ChannelSearchItem[] = [];
  const seen = new Set<string>();
  const push = (item: ChannelSearchItem) => {
    if (seen.has(item.id)) {
      return;
    }
    seen.add(item.id);
    items.push(item);
  };

  /* SR4 — the page's list first, so its name and logo are the ones kept when a Genre Pack offers
     the same subscription. */
  for (const subscription of source.subscriptions ?? []) {
    push(subscriptionItem(subscription, group));
  }
  for (const pack of source.packs ?? []) {
    for (const subscription of pack.subscriptions) {
      push(subscriptionItem(subscription, group));
    }
  }

  for (const channel of source.channels ?? []) {
    push(channelItem(channel, badgeLabels));
  }
  for (const channel of source.localChannels ?? []) {
    push(channelItem(channel, badgeLabels));
  }
  for (const channel of source.rsns ?? []) {
    push(channelItem(channel, badgeLabels));
  }

  return items;
}

/**
 * What one ZIP change does to the selection. `MatchedIds` is the seam WAT-12336/WAT-12337 filter
 * on, not `selectedIds`.
 */
export interface LineupSearchReconciliation {
  /** One id per surviving selection, in the order the reader built them. */
  selectedIds: string[];
  /** ZP12's third clause — "the chip stays". */
  carried: ChannelSearchItem[];
  /** Keyed by the surviving selected id. An entry of `[]` is a carried
   *  selection: the chip is there and nothing in this market matches it. */
  matchedIds: Record<string, string[]>;
}

/**
 * ZP12 — whether two item lists describe the same market. A new array is not a new market, and only
 * a new market may reconcile.
 */
export function isSameLineupSearchMarket(
  previousItems: readonly ChannelSearchItem[],
  nextItems: readonly ChannelSearchItem[]
): boolean {
  if (previousItems.length !== nextItems.length) {
    return false;
  }
  const previousIds = new Set(previousItems.map(item => item.id));
  const nextIds = new Set(nextItems.map(item => item.id));
  if (previousIds.size !== nextIds.size) {
    return false;
  }
  for (const id of nextIds) {
    if (!previousIds.has(id)) {
      return false;
    }
  }
  return true;
}

/**
 * ZP12 — a ZIP change replaces the item set, and the selections survive it. Yes, this compares
 * names, and it is the one place that may.
 */
export function reconcileLineupSearchSelection(
  previousItems: readonly ChannelSearchItem[],
  nextItems: readonly ChannelSearchItem[],
  selectedIds: readonly string[]
): LineupSearchReconciliation {
  const nextById = new Map(nextItems.map(item => [item.id, item]));
  const previousById = new Map(previousItems.map(item => [item.id, item]));

  /* Built once rather than scanned per selection: a market carries a few
     thousand rows and a reader can hold many chips. */
  const nextByName = new Map<string, ChannelSearchItem[]>();
  for (const item of nextItems) {
    const key = nameKey(item);
    const bucket = nextByName.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      nextByName.set(key, [item]);
    }
  }

  const resolved: string[] = [];
  const carried: ChannelSearchItem[] = [];
  const matchedIds: Record<string, string[]> = {};
  const taken = new Set<string>();

  const keep = (id: string, matches: string[]) => {
    if (taken.has(id)) {
      return;
    }
    taken.add(id);
    resolved.push(id);
    matchedIds[id] = matches;
  };

  for (const selectedId of selectedIds) {
    /* Already carried into this list by an earlier selection re-anchoring to
       the same row — ZP12 gives one chip per selection, never two. */
    if (taken.has(selectedId)) {
      continue;
    }

    if (nextById.has(selectedId)) {
      /* The same channel is in the new market. SR6 keeps it to itself. */
      keep(selectedId, [selectedId]);
      continue;
    }

    const previous = previousById.get(selectedId);
    if (!previous) {
      /* Neither market knows it. There is no label to carry and so no chip to
         draw — dropping it is the only honest outcome. */
      continue;
    }

    const candidates = nextByName.get(nameKey(previous)) ?? [];
    if (candidates.length === 0) {
      /* ZP12 clause 3 — the chip stays; GQ5/GP10's "not available" message is
         the view's, not this component's. */
      carried.push(previous);
      keep(selectedId, []);
      continue;
    }

    /* Clauses 1 and 2 — exactly one match moves the selection to it; several
       keep ONE chip that stands for all of them. */
    keep(candidates[0].id, candidates.map(candidate => candidate.id));
  }

  return {
    selectedIds: resolved,
    carried,
    matchedIds
  };
}

/**
 * The name and the kind. ZP12 compares names "without regard to case"; a subscription must never
 * re-anchor to a channel that happens to share a name, which is why the namespace is part of the
 * key rather than stripped out.
 */
function nameKey(item: ChannelSearchItem): string {
  const kind = parseLineupSearchId(item.id)?.kind ?? "";
  return `${kind}\u0000${item.label.toLowerCase()}`;
}

/** SR10 — the authored limit, as a usable number or as no limit at all. */
export function lineupSelectionLimitOf(value: unknown): number | undefined {
  const limit = typeof value === "string" ? Number(value.trim()) : value;
  if (typeof limit !== "number" || !Number.isFinite(limit) || limit < 1) {
    return undefined;
  }
  return Math.floor(limit);
}

/**
 * SR11 — the resolved selection, as WAT-12336 (Signature grid under search) and WAT-12337 (Genre
 * Packs under search) read it.
 */
export interface LineupSearchSelection {
  /** One per chip, namespaced. The reader's selection, in their order. */
  selectedIds: string[];
  /** ZP12 — every row in THIS market a chip stands for, keyed by chip. */
  matchedIds: Record<string, string[]>;
  /**
   * Each chip's own wording, keyed by chip, for a consumer that has to name one. Keyed like
   * `matchedIds` and never index-parallel with `selectedIds`.
   */
  selectedLabels: Record<string, string>;
  /** CCIDs to filter channel rows on — `matchedIds` flattened and unwrapped. */
  channelIds: string[];
  /** Epoch plan codes to filter subscription rows on. */
  subscriptionIds: string[];
  /**
   * GQ/GP10 — whether a search is filtering the views. Selections, not the typed string: the
   * molecule never reports the query upward (`channel-search.client.tsx:55-59`), so the chips are
   * the whole signal that crosses this seam.
   */
  isActive: boolean;
}

/** The inert selection — what a consumer rendered outside a provider sees. */
export const EMPTY_LINEUP_SEARCH_SELECTION: LineupSearchSelection = {
  selectedIds: [],
  matchedIds: {},
  selectedLabels: {},
  channelIds: [],
  subscriptionIds: [],
  isActive: false
};

/** `offered` is required, not an optional third argument. */
export function lineupSearchSelectionOf(
  selectedIds: readonly string[],
  matchedIds: Record<string, string[]>,
  offered: readonly ChannelSearchItem[]
): LineupSearchSelection {
  const channelIds: string[] = [];
  const subscriptionIds: string[] = [];
  const seen = new Set<string>();
  const labels = new Map(offered.map(item => [item.id, item.label]));
  const selectedLabels: Record<string, string> = {};

  for (const selectedId of selectedIds) {
    /* Feed strings are already through `plainText` on the way in (`channelItem`), so a trim is all
       that is left — and a row whose wording trimmed away gets no entry rather than an empty name a
       consumer would announce as a blank term. */
    const label = (labels.get(selectedId) ?? "").trim();
    if (label !== "") {
      selectedLabels[selectedId] = label;
    }

    /* A chip with no entry stands for itself — that is every selection made
       straight from the list, which is the common case. */
    for (const matched of matchedIds[selectedId] ?? [selectedId]) {
      if (seen.has(matched)) {
        continue;
      }
      seen.add(matched);
      const ref = parseLineupSearchId(matched);
      if (ref?.kind === "channel") {
        channelIds.push(ref.id);
      } else if (ref?.kind === "subscription") {
        subscriptionIds.push(ref.id);
      }
    }
  }

  return {
    selectedIds: [...selectedIds],
    matchedIds,
    selectedLabels,
    channelIds,
    subscriptionIds,
    isActive: selectedIds.length > 0
  };
}
